import Ad, { clearKeyValuePair, setKeyValuePair } from "@gik/ads/components/Ad";
import ZonesMap from "@gik/ads/maps/ZonesMap";
import { usePosts } from "@gik/api/posts/posts";
import { usePostsRelated } from "@gik/api/posts/postsRelated";
import type { RelationFilter } from "@gik/api/posts/PostsUrlBuilder";
import { ArticleBody } from "@gik/blog/components/ArticleBody/ArticleBody";
import { ArticleHeaderSection } from "@gik/blog/components/ArticleHeaderSection/ArticleHeaderSection";
import { ArticlesGrid } from "@gik/blog/components/ArticlesGrid/ArticlesGrid";
import type Article from "@gik/blog/models/Article";
import { withBlogArticlePageMetaTags } from "@gik/blog/utils/MetaTags";
import WordpressConverter from "@gik/blog/utils/WordpressConverter";
import { DynamicComponent } from "@gik/core/components/DynamicComponent";
import { SkeletonNetworkError, SkeletonNetworkRetryMessage } from "@gik/core/components/NetworkError/NetworkError";
import Link from "@gik/core/components/Link.tsx";
import { withTitle } from "@gik/core/hocs/withTitle";
import type WordpressPost from "@gik/core/models/wordpress/WordpressPost";
import { NotFoundPage } from "@gik/core/pages/NotFound/NotFoundPage";
import { useBemCN } from "@gik/core/utils/bemBlock";
import getPreviewMode, { PreviewModeType } from "@gik/core/utils/PreviewMode";
import { ArticleStructuredData } from "@gik/seo";
import { ArticleAuthorLink } from "@gik/ui-legacy/gik/ArticleAuthorLink";
import { PageSection } from "@gik/ui-legacy/gik/PageSection";
import { Separator } from "@gik/ui-legacy/Separator";
import { ParagraphBone, FrameBone } from "@gik/ui-legacy/SkeletonLoader";
import * as NetworkStoreApi from "@gik/core/stores/NetworkStore.ts";
import React from "react";

type InnerArticlePageServerProps = {
	relatedArticles: WordpressPost[];
};

export type ArticlePageProps = {
	article: Article;
	fallbackData?: ArticlePageServerProps;
};

const blockName = "article-page";

// Skeleton component for the article content
function ArticleContentSkeleton() {
	const bem = useBemCN(blockName);

	return (
		<article {...bem()}>
			<PageSection {...bem("article-body", null, "justify-center flex")}>
				<div className="flex gap-4 mb-6">
					<FrameBone className="w-48 h-48 flex-shrink-0" />
					<div className="flex-1">
						<ParagraphBone words={12} className="mb-2" />
						<ParagraphBone words={6} />
					</div>
				</div>
			</PageSection>
			<PageSection {...bem("article-body")} noPadTop>
				<div {...bem("side-column", [{ left: true }])} />
				<main {...bem("main")}>
					<ParagraphBone words={5} style={{ marginBottom: 16 }} />
					<Separator variant={"neutral-300"} thickness={"3x"} size={"lg"} />

					<ParagraphBone words={80} /> <br/>
					<ParagraphBone words={75} /><br/>
					<ParagraphBone words={85} />
				</main>
				<div {...bem("side-column", [{ right: true }])} />
			</PageSection>
		</article>
	);
}

function ArticlePageComp({ article, fallbackData }: ArticlePageProps) {
	const bem = useBemCN(blockName);
	const blogPreviewMode = getPreviewMode(PreviewModeType.Blog);

	React.useEffect(() => {
		if (article?.taxonomyItemName) {
			setKeyValuePair(article.taxonomyItemName);
		}

		return () => {
			clearKeyValuePair();
		};
	}, [article?.taxonomyItemName]);

	if (article?.isPreviewOnly && !blogPreviewMode) {
		return <NotFoundPage />;
	}

	// FIXME: need to add reading progress
	// meta lives in <ArticleMeta> (hoisted above DynamicComponent) so it renders at SSR; not here.
	return (
		<ArticleStructuredData article={article}>
			<article {...bem()}>
				<ArticleHeaderSection article={article} />

				<PageSection {...bem("article-body")} noPadTop>
					<div {...bem("side-column", [{ left: true }])} />
					<main {...bem("main")}>
						<div {...bem("author-link")}>
							<ArticleAuthorLink
								authorFullName={article?.authorFullName}
								authorSlug={article?.authorSlug}
								articleId={article?.articleId}
								date={article?.date}
							/>
						</div>

						<Separator variant={"neutral-300"} thickness={"3x"} size={"lg"} />

						{article && <ArticleBody article={article} />}

						<Separator variant={"neutral-300"} thickness={"3x"} size={"lg"} />

						<p {...bem("terms")}>
							Give InKind does not provide medical advice, diagnosis, or
							treatment. We have an affiliate relationship with many of the
							advertisers on our site, and may receive a commission from any
							products purchased from links in this article. See{" "}
							<Link href="/terms-service">Terms {"&"} Conditions</Link>.
						</p>

						<Ad
							zone={ZonesMap.ArticleZones.Bottom}
							mobileZone={ZonesMap.ArticleZones.BottomMobile}
							override={article?.advertising}
						/>
					</main>
					<div {...bem("side-column", [{ right: true }])}>
						<Ad
							zone={ZonesMap.ArticleZones.Right}
							{...bem("columns-3")}
							adType="vertical"
							override={article?.advertising}
						/>
					</div>
				</PageSection>
			</article>
		</ArticleStructuredData>
	);
}

// renders article <head> meta from SWR fallback data at SSR, outside the
// network-gated DynamicComponent (which only renders its children after a client-side
// fetch marks success). Same requestKey => SWR dedupes, no extra request.
function ArticleMeta({ slug }: { slug: string }) {
	const requestKey = `article-page-${slug}`;
	const { data } = usePosts({ slug, perPage: 1 }, { requestKey });
	const post = (data as WordpressPost[] | undefined)?.[0];
	if (!post) return null;
	return withBlogArticlePageMetaTags(WordpressConverter.convertPost(post), null);
}

export type ArticlePageServerProps = InnerArticlePageServerProps & {
	article: WordpressPost[];
};

export type ArticlePageWithDataProps = {
	slug: string;
	fallbackData?: ArticlePageServerProps;
} & Omit<ArticlePageProps, "fallbackData" | "article">;

export function ArticlePageWithData({
	slug,
	fallbackData,
}: ArticlePageWithDataProps) {
	const requestKey = `article-page-${slug}`;

	return (
		<>
			{/* SSR-rendered SEO meta, independent of the network-gated fetch below */}
			<ArticleMeta slug={slug} />
			{/* Main Article Content Section */}
			<DynamicComponent<WordpressPost[], Article>
				height={600}
				requestKey={requestKey}
				useNetworkStore={true}
				fetch={function useFetch() {
					return usePosts(
						{
							slug,
							perPage: 1,
						},
						{
							fallbackData: fallbackData?.article,
							requestKey,
						},
					);
				}}
				transform={(data) => WordpressConverter.convertPost(data[0])}
				loadingComponent={() => (
					<div className="relative">
						<ArticleContentSkeleton />
						{NetworkStoreApi.shouldShowRetryMessage(requestKey) && !NetworkStoreApi.isError(requestKey) && (
							<div className="absolute inset-0 flex items-center justify-center z-10">
								<SkeletonNetworkRetryMessage />
							</div>
						)}
						{NetworkStoreApi.isError(requestKey) && (
							<div className="absolute inset-0 flex items-center justify-center z-10">
								<SkeletonNetworkError
									onReload={() => {
										NetworkStoreApi.reload(requestKey);
									}}
								/>
							</div>
						)}
					</div>
				)}
			>
				{({ transformed: article }) =>
					withTitle(
						{
							title: article?.title,
							fallback: fallbackData?.article?.[0]?.title?.rendered,
						},
						<>
							<ArticlePageComp article={article} fallbackData={fallbackData} />

							{/* Related Articles Section - Renders Independently */}
							<div className="relative">
								<DynamicComponent<WordpressPost[], Article[]>
									height={200}
									requestKey={`article-related-${article?.articleSlug}`}
									useNetworkStore={true}
									fetch={function useFetch() {
										return usePostsRelated(
											{
												relationFilters: [
													{
														taxonomy: article?.primaryTaxonomySlug,
														terms: [article?.taxonomyItemSlug],
													},
												] as RelationFilter<string[]>[],
											},
											{
												fallbackData: fallbackData?.relatedArticles,
												requestKey: `article-related-${article?.articleSlug}`,
											},
										);
									}}
									transform={(data) =>
										data
											.map(WordpressConverter.convertPost)
											.filter(WordpressConverter.filterOutPreviewContent)
									}
									loadingComponent={() => (
										<PageSection>
											<h2 style={{ marginBottom: 24 }}>Related Articles</h2>
											<ArticlesGrid articles={Array.from(new Array(4))} skeleton />
										</PageSection>
									)}
								>
									{({ transformed: articles }) => {
										const blogPreviewMode = getPreviewMode(PreviewModeType.Blog);
										const filteredArticles = articles
											?.filter(
												(_article) =>
													!_article.isPreviewOnly ||
													_article.isPreviewOnly === blogPreviewMode,
											)
											.filter(
												(_article) =>
													_article.articleSlug !== article.articleSlug,
											)
											.slice(0, 4);

										return (
											<PageSection>
												<h2 style={{ marginBottom: 24 }}>Related Articles</h2>
												<ArticlesGrid articles={filteredArticles} />
											</PageSection>
										);
									}}
								</DynamicComponent>

								{NetworkStoreApi.shouldShowRetryMessage(`article-related-${article?.articleSlug}`) &&
								 !NetworkStoreApi.isError(`article-related-${article?.articleSlug}`) && (
									<div className="absolute inset-0 flex items-center justify-center z-10">
										<SkeletonNetworkRetryMessage />
									</div>
								)}
								{NetworkStoreApi.isError(`article-related-${article?.articleSlug}`) && (
									<div className="absolute inset-0 flex items-center justify-center z-10">
										<SkeletonNetworkError
											onReload={() => {
												NetworkStoreApi.reload(`article-related-${article?.articleSlug}`);
											}}
										/>
									</div>
								)}
							</div>
						</>,
					)
				}
			</DynamicComponent>
		</>
	);
}
